import { useState, useEffect, useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import ReactGA from "react-ga4";
import "bootstrap/dist/css/bootstrap.min.css";

import getTheme from "./theme/theme";
import ColorModeContext from "./utils/ColorModeContext";
import Layout from "./layout/Layout";
import StripePricing from "./pages/PricingTable";
import PasswordResetPage from "./pages/PasswordReset";
import RegisterPage from "./pages/MainView/RegisterPage";
import { BasePage } from "./pages/BasePage";
import FundraisingRepresentativePage from "./pages/Dashboards/FundraiserRep/FundraiserRepDashboard";
import LoginPage from "./pages/MainView/LoginPage";
import OrgInputFormPage from "./pages/MainView/OrgInfoInputPage";
import RevisedStorefront from "./components/RevisedStorefront/RevisedStorefront";
import HomePage from "./pages/HomePage";
import { AboutUs } from "./pages/AboutUs";
import OrganizationInfo from "./pages/Starting-Your-Fundraiser/Step1-OrganizationInfo";
import FundraiserInfo from "./pages/Starting-Your-Fundraiser/Step2-FundraiserInfo";
import FundraiserDivision from "./pages/Starting-Your-Fundraiser/Step3-FundraiserDivisions";
import FundraiserAssignParticipants from "./pages/Starting-Your-Fundraiser/Step4-AssignParticipants";
import FAQ from "./components/FAQSection/FAQ";
import EnterFundraiserID from "./pages/EnterFundraiserID";
import ParticipantJoinFundraiser from "./pages/Participant/JoinFundraiser";
import ParticipantAddContacts from "./pages/Participant/AddContacts";
import SignUpPage from "./pages/MainView/MamFamAccountSignUp";
import ContactUsPage from "./pages/ContactUs";
import WarehouseOrderPage from "./pages/Warehouse/WarehouseOrder";
import WarehouseOrderDisplay from "./pages/Warehouse/AllOrderDisplay";

import { CartProvider } from "./contexts/Cart/CartContext";
import CartDrawer from "./contexts/Cart/CartDrawer";

import CartDisplayPage from "./pages/Checkout/CartDisplay";
import GeneralDashboard from "./pages/RefactoredDashboards/GeneralDashboard";
import RefactoredParticipantDashboard from "./pages/RefactoredDashboards/ParticipantDashboard";
import TeamAdminDashboard from "./pages/RefactoredDashboards/TeamAdminDashboard";
import CoordinatorDashboard from "./pages/RefactoredDashboards/CoordinatorDashboard";

import { PopupProvider } from "./components/Popup/PopupContext";
import GlobalPopupHandler from "./components/Popup/GlobalPopupHandler";

import { CheckoutSuccess } from "./pages/Checkout/CheckoutSuccess";
import RefactoredSingleProductPage from "./pages/ImprovedShopPage/RefactoredSingleProductPage";
import CustomBottlePage from "./pages/CustomizeBottle";
import CustomClothingPage from "./pages/CustomizeClothing";
import { getCurrentlyLoggedInUser } from "./CommonFunctions";
import { LoggedInAccount } from "./Interfaces/AccountUsers";
import SampleRequestForm from "./pages/GoogleForms/SampleRequestForm";
import NewFundraiserRequest from "./pages/GoogleForms/NewFundraiserRequest";
import UnsubscribeForm from "./pages/EmailUnsubscribe";
import PrivacyPolicy from "./pages/Privacy Policy";
import EditParticipantsAdminPage from "./pages/EditParticipants/AdminEditParticipants";
import CustomOrganizationMamFamAuth from "./pages/MainView/CustomOrganizations/CustomMamFamSignup";
import LogoRequestPage from "./pages/LogoRequest/LogoRequest";
import ImprovedShopPage from "./pages/ImprovedShopPage/ImprovedShopPage";
import RefactoredCheckoutPage from "./components/Storefront/RefactoredCheckoutPage";
import RefactoredHomePage from "./pages/RefactoredHomePage/RefactoredHomePage";
import InventoryStockDisplay from "./pages/Warehouse/InventoryStock";
import SampleStaticShop from "./pages/SamplePages/SampleStaticShop";
import OrderDisplayPage from "./pages/Customer/DisplayOrderContents";
import InventoryChanges from "./pages/Warehouse/InventoryChanges";
import InventoryRemaining from "./pages/Warehouse/InventoryRemaining";

const NotFound = () => {
  useEffect(() => {
    document.title = "MamFam Not Found";
  }, []);

  return (
    <div style={{ textAlign: "center", marginTop: "20vh" }}>
      <h3>404 - Not found</h3>
    </div>
  );
};

const App = (): JSX.Element => {
  const [mode, setMode] = useState("light");
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        window.localStorage.setItem(
          "themeMode",
          mode === "dark" ? "light" : "dark",
        );
        setMode((prevMode) => (prevMode === "dark" ? "light" : "dark"));
      },
    }),
    [mode],
  );
  // quick and dirty method, we could also set a delay on rendering until
  //    isLoggedIn is set, but I feel that sometimes doesn't work the best.
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [user, setUser] = useState<LoggedInAccount>();

  const handleCheckLogin = async () => {
    try {
      const currentLogin = await getCurrentlyLoggedInUser();
      setUser(currentLogin);
      setIsLoggedIn(true);
    } catch (error) {
      setIsLoggedIn(false);
    }
  };

  useEffect(() => {
    try {
      const localTheme = window.localStorage.getItem("themeMode");
      localTheme ? setMode(localTheme) : setMode("light");
    } catch {
      setMode("light");
    }
    handleCheckLogin();

    ReactGA.initialize("YOUR-GOOGLE-ANALYTICS-MEASUREMENT-ID");
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });
  }, []);

  return (
    <CartProvider>
      <HelmetProvider>
        <ColorModeContext.Provider value={colorMode}>
          <ThemeProvider theme={getTheme(mode)}>
            <CssBaseline />
            <BrowserRouter>
              <PopupProvider>
                <GlobalPopupHandler />
                <CartDrawer />
                <Layout>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route
                      path="/refactored-home-page"
                      element={<RefactoredHomePage />}
                    />
                    <Route
                      path="/signup/:fundraiserID/:type"
                      element={<CustomOrganizationMamFamAuth />}
                    />
                    <Route path="/about-us" element={<AboutUs />} />
                    <Route path="/contact-us" element={<ContactUsPage />} />
                    <Route path="/storefront" element={<RevisedStorefront />} />

                    <Route path="/pricing-table" element={<StripePricing />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignUpPage />} />
                    <Route
                      path="/refactored-product/:sku"
                      element={<RefactoredSingleProductPage />}
                    />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="*" element={<NotFound />} />
                    <Route
                      path="/dashboard"
                      element={<Navigate to="/dashboard/user" replace />}
                    />
                    <Route
                      path="/reset-password"
                      element={<PasswordResetPage />}
                    />
                    <Route path="/faq" element={<FAQ />} />
                    <Route
                      path="/checkout/success"
                      element={<CheckoutSuccess />}
                    />
                    <Route
                      path="/participant-shop/part-id/:participantID"
                      element={<ImprovedShopPage />}
                    />
                    <Route
                      path="/order/order-contents/:orderID"
                      element={<OrderDisplayPage />}
                    />
                    <Route
                      path="/improved-participant-shop/part-id/:participantID"
                      element={<ImprovedShopPage />}
                    />
                    <Route
                      path="/sample-site/participant-shop"
                      element={<SampleStaticShop />}
                    />
                    <Route
                      path="/register-or-shop"
                      element={<EnterFundraiserID />}
                    />
                    <Route
                      path="/shop"
                      element={<Navigate to="/register-or-shop" replace />}
                    />
                    <Route
                      path="/sample-request-form"
                      element={<SampleRequestForm />}
                    />
                    <Route
                      path="/email/unsubscribe"
                      element={<UnsubscribeForm />}
                    />
                    <Route
                      path="/new-fundraiser-request"
                      element={<NewFundraiserRequest />}
                    />
                    <Route
                      path="/checkout/cart"
                      element={<CartDisplayPage />}
                    />
                    <Route
                      path="/checkout"
                      element={<RefactoredCheckoutPage />}
                    />
                    <Route
                      path="/storefront/customize-bottle/"
                      element={<CustomBottlePage />}
                    />
                    <Route
                      path="/storefront/customize-clothing/"
                      element={<CustomClothingPage />}
                    />
                    <Route
                      path="/privacy-policy/"
                      element={<PrivacyPolicy />}
                    />

                    <Route
                      path="/fundraiser/join-fundraiser/:fundraiserID"
                      element={
                        <BasePage permission={isLoggedIn}>
                          <ParticipantJoinFundraiser />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/fundraiser/edit-participants/:fundraiserID"
                      element={
                        <BasePage permission={isLoggedIn}>
                          <EditParticipantsAdminPage />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/warehouse/orders/"
                      element={
                        <BasePage
                          permission={
                            isLoggedIn && user !== undefined && user.is_staff
                          }
                        >
                          <WarehouseOrderPage />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/warehouse/orders/:fundraiserID"
                      element={
                        <BasePage
                          permission={
                            isLoggedIn && user !== undefined && user.is_staff
                          }
                        >
                          <WarehouseOrderPage />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/warehouse/orders/all"
                      element={
                        <BasePage
                          permission={
                            isLoggedIn && user !== undefined && user.is_staff
                          }
                        >
                          <WarehouseOrderDisplay />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/warehouse/inventory-stock"
                      element={
                        <BasePage
                          permission={
                            isLoggedIn && user !== undefined && user.is_staff
                          }
                        >
                          <InventoryStockDisplay />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/warehouse/inventory-changes"
                      element={
                        <BasePage
                          permission={
                            isLoggedIn && user !== undefined && user.is_staff
                          }
                        >
                          <InventoryChanges />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/warehouse/inventory-remaining"
                      element={
                        <BasePage
                          permission={
                            isLoggedIn && user !== undefined && user.is_staff
                          }
                        >
                          <InventoryRemaining />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/create-fundraiser/org-info"
                      element={
                        <BasePage
                          permission={
                            isLoggedIn && user !== undefined && user.is_staff
                          }
                        >
                          <OrganizationInfo />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/create-fundraiser/fundraiser-info"
                      element={
                        <BasePage
                          permission={
                            isLoggedIn && user !== undefined && user.is_staff
                          }
                        >
                          <FundraiserInfo />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/create-fundraiser/division-info"
                      element={
                        <BasePage permission={isLoggedIn}>
                          <FundraiserDivision />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/create-fundraiser/assign-participants"
                      element={
                        <BasePage permission={isLoggedIn}>
                          <FundraiserAssignParticipants />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/input-org-info"
                      element={
                        <BasePage
                          permission={
                            isLoggedIn && user !== undefined && user.is_staff
                          }
                        >
                          <OrgInputFormPage />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/fundraiser/participant/add-contacts/:participantIds"
                      element={
                        <BasePage permission={isLoggedIn}>
                          <ParticipantAddContacts />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/dashboard/user"
                      element={
                        <BasePage permission={isLoggedIn}>
                          <GeneralDashboard />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/dashboard/participant/:linkExtension?"
                      element={
                        <BasePage permission={isLoggedIn}>
                          <RefactoredParticipantDashboard />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/dashboard/teamAdmin/:linkExtension?"
                      element={
                        <BasePage permission={isLoggedIn}>
                          <TeamAdminDashboard />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/dashboard/coord/:linkExtension?"
                      element={
                        <BasePage permission={isLoggedIn}>
                          <CoordinatorDashboard />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/dashboard/fundraising-rep/:linkExtension?"
                      element={
                        <BasePage permission={isLoggedIn}>
                          <FundraisingRepresentativePage />
                        </BasePage>
                      }
                    />
                    <Route
                      path="/fundraiser/logo-request/"
                      element={
                        <BasePage
                          permission={
                            isLoggedIn && user !== undefined && user.is_staff
                          }
                        >
                          <LogoRequestPage />
                        </BasePage>
                      }
                    />
                  </Routes>
                </Layout>
              </PopupProvider>
            </BrowserRouter>
          </ThemeProvider>
        </ColorModeContext.Provider>
      </HelmetProvider>
    </CartProvider>
  );
};

export default App;
